// TestQuestion.jsx
import React from 'react';

/**
 * Component for rendering a single test question
 * @param {Object} props
 * @param {number} props.questionNumber - The sequential number of the question
 * @param {Object} props.question - The question object containing data
 * @param {string} props.question.id - Unique identifier for the question
 * @param {string} props.question.text - The question text
 * @param {string} props.question.type - Question type (multiple_choice, essay, etc.)
 * @param {Array} [props.question.options] - Options for multiple choice questions
 * @param {string} props.value - Current answer value
 * @param {Function} props.onChange - Function to call when answer changes
 */
const TestQuestion = ({ questionNumber, question, value, onChange }) => {
  const renderQuestionInput = () => {
    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="question-options">
            {question.options.map((option, index) => (
              <div className="option" key={`${question.id}-option-${index}`}>
                <label className="option-label">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={value === option}
                    onChange={(e) => onChange(e.target.value)}
                    className="option-input"
                  />
                  <span className="option-text">{option}</span>
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'checkbox':
        // For multiple selection questions
        const selectedValues = value ? value.split(',') : [];
        return (
          <div className="question-options">
            {question.options.map((option, index) => (
              <div className="option" key={`${question.id}-option-${index}`}>
                <label className="option-label">
                  <input
                    type="checkbox"
                    name={`question-${question.id}-option-${index}`}
                    value={option}
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      const newSelectedValues = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter(val => val !== option);
                      onChange(newSelectedValues.join(','));
                    }}
                    className="option-input"
                  />
                  <span className="option-text">{option}</span>
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'essay':
        return (
          <textarea
            className="essay-input"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={6}
            placeholder="Type your answer here..."
          />
        );
      
      case 'short_answer':
        return (
          <input
            type="text"
            className="short-answer-input"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer here..."
          />
        );
      
      case 'numeric':
        return (
          <input
            type="number"
            className="numeric-input"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter a number"
          />
        );
        
      default:
        return (
          <div className="unsupported-question-type">
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  return (
    <div className="question-container">
      <div className="question-header">
        <h3 className="question-number">Question {questionNumber}</h3>
      </div>
      
      <div className="question-content">
        <p className="question-text">{question.text}</p>
        
        {question.image && (
          <div className="question-image-container">
            <img 
              src={question.image} 
              alt={`Image for question ${questionNumber}`} 
              className="question-image" 
            />
          </div>
        )}
        
        <div className="question-input-container">
          {renderQuestionInput()}
        </div>
      </div>
    </div>
  );
};

export default TestQuestion;